import polylabel from '@mapbox/polylabel';
import booleanPointInPolygon from '@turf/boolean-point-in-polygon';
import { polygon as turfPolygon } from '@turf/helpers';
import { COLOR } from 'const/color';
import { Feature, FeatureCollection, Geometry } from 'geojson';
import hull from 'hull.js';
import mapboxgl from 'mapbox-gl';
import { getBundleColor } from '../../../oldPlanner/utils/bundleDraft';
import { PlannerBundle, PlannerOrder } from '../../utils/usePlannerData';

export function ordersInsidePolygon(
  orders: GeoOrders,
  polygon: FeatureCollection<
    Geometry,
    {
      [name: string]: any;
    }
  >
): PlannerOrder[] {
  if (
    polygon.features.length > 0 &&
    polygon.features[0].geometry.type === 'Polygon'
  ) {
    const polygonAsFeature = turfPolygon(
      polygon.features[0].geometry.coordinates
    );

    const ordersInsidePolygon = [];

    orders.features.forEach(orderFeature => {
      const coordinates =
        orderFeature.geometry.type === 'Point' &&
        orderFeature.geometry.coordinates;
      if (booleanPointInPolygon(coordinates, polygonAsFeature)) {
        ordersInsidePolygon.push(...orderFeature.properties.orders);
      }
    });

    return ordersInsidePolygon;
  }
  return [];
}

export function geoJsonPropertiesToPlannerOrder(properties: {
  [name: string]: any;
}): PlannerOrder[] {
  return JSON.parse(properties.orders);
}

export function getProperties<T>(
  map: mapboxgl.Map,
  target: string,
  event: mapboxgl.MapMouseEvent & {
    features?: mapboxgl.MapboxGeoJSONFeature[];
  } & mapboxgl.EventData
): T {
  const features = map.queryRenderedFeatures(event.point, {
    layers: [target],
  });
  const properties = features?.[0]?.properties;
  let formattedProperties: Record<string, unknown> = {};

  Object.keys(properties).forEach(key => {
    let value: unknown;
    try {
      value = JSON.parse(properties[key]);
    } catch (_) {
      value = properties[key];
    }

    formattedProperties = { ...formattedProperties, [key]: value };
  });

  return formattedProperties as T;
}

type Properties = {
  orders: PlannerOrder[];
  window: string | string[];
  markerColor?: string;
  numberOfOrders?: number;
};

type GeoFeature = Feature & {
  properties: Properties;
};

export type GeoOrders = FeatureCollection & {
  features: GeoFeature[];
};

type BundledOrdersFeature = Feature & {
  properties: {
    orders: PlannerOrder[];
    numberOfOrders: number;
    markerColor: string;
    textColor: string;
  };
};

export type BundledOrdersFeatureCollection = FeatureCollection & {
  features: BundledOrdersFeature[];
};

export type BundleProperties = {
  bundleId: string | number;
  numberOfOrders: number;
  markerColor: string;
  textColor: string;
  polygon: number[][];
};

export type BundleFeature = Feature & {
  properties: BundleProperties;
};

export type BundlesFeatureCollection = FeatureCollection & {
  features: BundleFeature[];
};

export function convertPlannerBundleToGeoBundleOrders(
  bundles: PlannerBundle[]
): {
  bundledOrders: BundledOrdersFeatureCollection;
  bundles: BundlesFeatureCollection;
} {
  const bundleOrdersFeatureCollection: BundledOrdersFeatureCollection = {
    type: 'FeatureCollection',
    features: [],
  };

  const bundlesFeatureCollection: BundlesFeatureCollection = {
    type: 'FeatureCollection',
    features: [],
  };

  bundles.forEach(bundle => {
    const points = bundle.orders.map(groupedOrders => [
      groupedOrders[0].destinationAddress.location.longitude,
      groupedOrders[0].destinationAddress.location.latitude,
      0.0,
    ]);
    const polygon = hull(points, 80) as unknown as number[][];
    const centerOfPolygon = polylabel([polygon]);

    bundlesFeatureCollection.features.push({
      type: 'Feature',
      properties: {
        bundleId: bundle.id,
        numberOfOrders: bundle.orders.length,
        markerColor: bundle.color,
        textColor: bundle.textColor,
        polygon: polygon,
      },
      geometry: {
        type: 'Point',
        coordinates: [centerOfPolygon[0], centerOfPolygon[1], 0.0],
      },
    });

    bundle.orders.forEach(groupedOrders => {
      const order = groupedOrders[0];

      bundleOrdersFeatureCollection.features.push({
        type: 'Feature',
        properties: {
          orders: groupedOrders,
          numberOfOrders: groupedOrders.length,
          markerColor: bundle.color,
          textColor: bundle.textColor,
          window: order.window,
        },
        geometry: {
          type: 'Point',
          coordinates: [
            order.destinationAddress.location.longitude,
            order.destinationAddress.location.latitude,
            0.0,
          ],
        },
      });
    });
  });

  return {
    bundledOrders: bundleOrdersFeatureCollection,
    bundles: bundlesFeatureCollection,
  };
}

export function convertOrdersToGeoOrder(
  orders: PlannerOrder[][],
  selectedOrders: string[]
): GeoOrders {
  const features: GeoFeature[] = orders.map((groupedOrders: PlannerOrder[]) => {
    const firstOrder = groupedOrders[0];
    const uniqueWindows: Set<string> = new Set();
    const bundleIdSet: Set<string> = new Set();

    let partialSelection = false;

    let selectedOrder = false;
    let selectedOrdersInGroup = 0;

    groupedOrders.map(groupedOrder => {
      uniqueWindows.add(groupedOrder.window);
      bundleIdSet.add(groupedOrder.bundleId);

      const orderIsSelected = selectedOrders.includes(groupedOrder.id);
      if (orderIsSelected) {
        selectedOrder = true;
        selectedOrdersInGroup++;
      }

      return groupedOrder;
    });

    /* Remove null from the `Set` variables */
    uniqueWindows.delete(null);
    bundleIdSet.delete(null);

    let markerColor = 'white';

    /* Marker Color for Selected Order */
    if (selectedOrder) {
      if (selectedOrdersInGroup / groupedOrders.length === 1) {
        /* Red = All orders are selected */
        markerColor = '#c71585';
      } else {
        /* Pink = Not all orders are selected */
        markerColor = '#ff6fff';
        partialSelection = true;
      }
    }

    /* Marker Color for Bundle */
    if (bundleIdSet.size > 0) {
      const singleBundle = bundleIdSet.size === 1;
      markerColor = singleBundle
        ? getBundleColor(firstOrder.bundleId)
        : COLOR.MULTI_BUNDLE;
    }

    const uniqueWindowsArr = [...uniqueWindows];
    const feature: GeoFeature = {
      type: 'Feature',
      properties: {
        orders: groupedOrders,
        numberOfOrders: groupedOrders.length,
        window: uniqueWindows.size > 1 ? uniqueWindowsArr : uniqueWindowsArr[0],
        markerColor,
        partialSelection,
      },
      geometry: {
        type: 'Point',
        coordinates: [
          firstOrder.destinationAddress.location.longitude,
          firstOrder.destinationAddress.location.latitude,
          0.0,
        ],
      },
    };
    return feature;
  });

  const geoJson: GeoOrders = {
    type: 'FeatureCollection',
    features,
  };

  return geoJson;
}
